import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import * as XLSX from 'xlsx';

const STATUS_COLORS = {
    'Clear': 'bg-emerald-100 text-emerald-700',
    'Pending Recovery': 'bg-yellow-100 text-yellow-700',
    'Has Debt': 'bg-red-100 text-red-700',
    'Under Review': 'bg-blue-100 text-blue-700',
};

const Employees = () => {
    const { authFetch, depos, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin';

    const [employees, setEmployees] = useState([]);
    const [systemUsers, setSystemUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [localDepos, setLocalDepos] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [isLossModalOpen, setIsLossModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

    const [empForm, setEmpForm] = useState({ name: '', position: '', base_salary: '0', depo_id: '', department_id: '', role: 'Employee', status: 'Active' });
    const [advForm, setAdvForm] = useState({ employee_id: '', amount: '', note: '', date: new Date().toISOString().slice(0,10) });
    const [lossForm, setLossForm] = useState({
        employee_id: '', depo_id: '', product_id: '', product_name: '',
        quantity: '', unit: 'unit', amount: '', reason: '', source: 'Manual Entry', date: new Date().toISOString().slice(0,10)
    });
    const [lossSearchTerm, setLossSearchTerm] = useState('');
    const [lossProducts, setLossProducts] = useState([]);

    const [editForm, setEditForm] = useState({ name: '', position: '', base_salary: '', depo_id: '', department_id: '', role: 'Employee', status: 'Active' });

    useEffect(() => {
        const delay = setTimeout(() => {
            if (lossSearchTerm.length >= 2) {
                authFetch(`/api/products?search=${encodeURIComponent(lossSearchTerm)}`)
                    .then(r => r.json()).then(d => setLossProducts(Array.isArray(d) ? d : []));
            } else {
                setLossProducts([]);
            }
        }, 300);
        return () => clearTimeout(delay);
    }, [lossSearchTerm]);

    const fetchEmployees = () => {
        authFetch('/api/employees').then(r => r.json()).then(d => setEmployees(Array.isArray(d) ? d : []));
    };
    const fetchSystemUsers = () => {
        authFetch('/api/users').then(r => r.json()).then(d => setSystemUsers(Array.isArray(d) ? d : []));
    };
    const fetchDepartments = () => {
        authFetch('/api/departments').then(r => r.json()).then(d => setDepartments(Array.isArray(d) ? d : []));
    };
    useEffect(() => { fetchEmployees(); fetchSystemUsers(); fetchDepartments(); }, []);

    const handleAddEmp = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/employees', { method: 'POST', body: JSON.stringify(empForm) });
        if (res.ok) { setIsAddModalOpen(false); fetchEmployees(); }
        else alert(t('Failed to add employee'));
    };

    const handleAddAdvance = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/employees?action=advance', { method: 'POST', body: JSON.stringify(advForm) });
        if (res.ok) { setIsAdvanceModalOpen(false); fetchEmployees(); setAdvForm({ employee_id: '', amount: '', note: '', date: new Date().toISOString().slice(0,10) }); }
        else alert(t('Failed to log advance'));
    };

    const handleAddLoss = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/employees?action=loss', { method: 'POST', body: JSON.stringify(lossForm) });
        if (res.ok) { setIsLossModalOpen(false); fetchEmployees(); setLossForm({ employee_id: '', depo_id: '', product_id: '', product_name: '', quantity: '', unit: 'unit', amount: '', reason: '', source: 'Manual Entry', date: new Date().toISOString().slice(0,10) }); setLossSearchTerm(''); }
        else alert(t('Failed to log loss'));
    };

    const handleEditEmp = async (e) => {
        e.preventDefault();
        const res = await authFetch(`/api/employees/${selectedEmployee.id}`, { method: 'PUT', body: JSON.stringify(editForm) });
        if (res.ok) { setIsViewModalOpen(false); fetchEmployees(); }
        else alert(t('Failed to update employee'));
    };

    const handleDeleteEmp = (id) => {
        setConfirmDialog({
            isOpen: true,
            message: t('Are you sure you want to delete this employee?'),
            onConfirm: async () => {
                const res = await authFetch(`/api/employees/${id}`, { method: 'DELETE' });
                if (res.ok) fetchEmployees();
                else alert(t('Delete failed'));
                setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
            }
        });
    };

    const handleExport = () => {
        const rows = employees.map(e => ({
            [t('Name')]: e.name,
            [t('Position')]: e.position,
            [t('Branch')]: e.depo_name || '—',
            [t('Branch Cost Value')]: parseFloat(e.depo_cost_value || 0).toFixed(2),
            [t('Manual Advances')]: parseFloat(e.total_advances || 0).toFixed(2),
            [t('Loss Deductions')]: parseFloat(e.total_losses || 0).toFixed(2),
            [t('Total Deductions')]: parseFloat(e.total_deductions || 0).toFixed(2),
            [t('Current Balance')]: parseFloat(e.balance || 0).toFixed(2),
            [t('Status')]: t(e.status),
        }));
        const totalRow = {
            [t('Name')]: t('TOTAL'),
            [t('Position')]: '',
            [t('Branch')]: '',
            [t('Branch Cost Value')]: employees.reduce((s, e) => s + parseFloat(e.depo_cost_value || 0), 0).toFixed(2),
            [t('Manual Advances')]: employees.reduce((s, e) => s + parseFloat(e.total_advances || 0), 0).toFixed(2),
            [t('Loss Deductions')]: employees.reduce((s, e) => s + parseFloat(e.total_losses || 0), 0).toFixed(2),
            [t('Total Deductions')]: employees.reduce((s, e) => s + parseFloat(e.total_deductions || 0), 0).toFixed(2),
            [t('Current Balance')]: employees.reduce((s, e) => s + parseFloat(e.balance || 0), 0).toFixed(2) + ' CFA',
            [t('Status')]: '',
        };
        const ws = XLSX.utils.json_to_sheet([...rows, totalRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Payroll Report');
        XLSX.writeFile(wb, `Payroll_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const content = `
            <html><head><title>Payroll Report</title>
            <style>
                body { font-family: sans-serif; padding: 20px; }
                h1 { font-size: 22px; margin-bottom: 4px; }
                p { font-size: 12px; color: #666; margin-bottom: 20px; }
                table { width: 100%; border-collapse: collapse; }
                th, td { border: 1px solid #ddd; padding: 8px; font-size: 11px; text-align: left; }
                th { background: #f5f5f5; font-weight: bold; }
                .neg { color: red; font-weight: bold; }
                .pos { color: green; font-weight: bold; }
                tfoot td { font-weight: bold; background: #eee; }
            </style></head>
            <body>
                <h1>${t('Payroll & Debt Tracking')}</h1>
                <p>${t('Generated')}: ${new Date().toLocaleString()}</p>
                <div className="w-full overflow-x-auto max-w-full">
<table>
                    <thead><tr>
                        <th>${t('Employee')}</th>
                        <th>${t('Branch')}</th>
                        <th>${t('Branch Cost')}</th>
                        <th>${t('Advances')}</th>
                        <th>${t('Losses')}</th>
                        <th>${t('Balance')}</th>
                        <th>${t('Status')}</th>
                    </tr></thead>
                    <tbody>
                        ${employees.map(e => `<tr>
                            <td>${e.name}<br><small>${e.position || ''}</small></td>
                            <td>${e.depo_name || '—'}</td>
                            <td>${parseFloat(e.depo_cost_value || 0).toLocaleString()} CFA</td>
                            <td class="neg">-${parseFloat(e.total_advances || 0).toLocaleString()}</td>
                            <td class="neg">-${parseFloat(e.total_losses || 0).toLocaleString()}</td>
                            <td class="${e.balance < 0 ? 'neg' : 'pos'}">${parseFloat(e.balance || 0).toLocaleString()} CFA</td>
                            <td>${t(e.status)}</td>
                        </tr>`).join('')}
                    </tbody>
                    <tfoot><tr>
                        <td colspan="2">${t('TOTALS')}</td>
                        <td>${employees.reduce((s,e) => s + parseFloat(e.depo_cost_value||0), 0).toLocaleString()} CFA</td>
                        <td>${employees.reduce((s,e) => s + parseFloat(e.total_advances||0), 0).toLocaleString()} CFA</td>
                        <td>${employees.reduce((s,e) => s + parseFloat(e.total_losses||0), 0).toLocaleString()} CFA</td>
                        <td>${employees.reduce((s,e) => s + parseFloat(e.balance||0), 0).toLocaleString()} CFA</td>
                        <td></td>
                    </tr></tfoot>
                </table>
</div>
            </body></html>
        `;
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.print();
    };

    // Summary stats
    const totalPayroll = employees.reduce((s, e) => s + parseFloat(e.depo_cost_value || 0), 0);
    const totalToRecover = employees.reduce((s, e) => s + parseFloat(e.total_deductions || 0), 0);
    const totalLosses = employees.reduce((s, e) => s + parseFloat(e.total_losses || 0), 0);
    const employeesWithDebt = employees.filter(e => e.status === 'Has Debt' || e.status === 'Pending Recovery').length;

    const columns = [
        {
            header: t('Employee'), accessor: 'name',
            render: r => (
                <div>
                    <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-800">{r.name}</p>
                        {r.role && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold">{r.role}</span>}
                    </div>
                    <p className="text-xs text-gray-500">{r.position || '—'} {r.department_name ? `• ${r.department_name}` : ''}</p>
                </div>
            )
        },
        {
            header: t('Branch'), accessor: 'depo_name',
            render: r => r.depo_name
                ? <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-lg text-xs font-bold">{r.depo_name}</span>
                : <span className="text-gray-400 text-xs italic">—</span>
        },
        {
            header: t('Branch Cost'), accessor: 'depo_cost_value',
            render: r => <span className="font-semibold">{parseFloat(r.depo_cost_value || 0).toLocaleString()} CFA</span>
        },
        {
            header: t('Advances'), accessor: 'total_advances',
            render: r => <span className="text-orange-600 font-medium">-{parseFloat(r.total_advances || 0).toLocaleString()}</span>
        },
        {
            header: t('Losses'), accessor: 'total_losses',
            render: r => <span className="text-red-500 font-medium">-{parseFloat(r.total_losses || 0).toLocaleString()}</span>
        },
        {
            header: t('Total Deductions'), accessor: 'total_deductions',
            render: r => <span className="text-red-700 font-bold">-{parseFloat(r.total_deductions || 0).toLocaleString()} CFA</span>
        },
        {
            header: t('Balance'), accessor: 'balance',
            render: r => (
                <span className={`font-black text-lg ${parseFloat(r.balance) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {parseFloat(r.balance || 0).toLocaleString()} CFA
                </span>
            )
        },
        {
            header: t('Status'), accessor: 'status',
            render: r => (
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                    {t(r.status)}
                </span>
            )
        },
        {
            header: t('Actions'), accessor: 'actions',
            render: r => (
                <div className="flex flex-wrap gap-1">
                    <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200" onClick={() => {
                        setSelectedEmployee(r);
                        setEditForm({ 
                            name: r.name, 
                            position: r.position, 
                            base_salary: r.base_salary, 
                            depo_id: r.depo_id || '', 
                            department_id: r.department_id || '', 
                            role: r.role || 'Employee', 
                            status: r.status || 'Active' 
                        });
                        setIsViewModalOpen(true);
                        // Fetch full details
                        authFetch(`/api/employees/${r.id}`).then(res => res.json()).then(d => setSelectedEmployee(d));
                    }}>{t('View')}</button>
                    {isAdmin && <>
                        <button className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200" onClick={() => {
                            setAdvForm({ ...advForm, employee_id: r.id });
                            setIsAdvanceModalOpen(true);
                        }}>{t('Advance')}</button>
                        <button className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200" onClick={() => {
                            setLossForm({ ...lossForm, employee_id: r.id, depo_id: r.depo_id || '' });
                            setIsLossModalOpen(true);
                        }}>{t('Loss')}</button>
                        <button className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200" onClick={() => handleDeleteEmp(r.id)}>{t('Del')}</button>
                    </>}
                </div>
            )
        },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Payroll & Debt Tracking')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Branch-based employee payroll, advances and loss/theft deductions')}</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <button onClick={handleExport} className="p-2 hover:bg-gray-100 rounded text-green-600 transition" title={t('Export to Excel')}>📊</button>
                    <button onClick={handlePrint} className="p-2 hover:bg-gray-100 rounded text-blue-600 transition" title={t('Print Report')}>🖨️</button>
                    {isAdmin && <>
                        <button onClick={() => setIsLossModalOpen(true)} className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700 transition font-bold">
                            🔴 {t('Log Loss / Theft')}
                        </button>
                        <button onClick={() => setIsAdvanceModalOpen(true)} className="bg-orange-500 text-white px-4 py-2 rounded shadow hover:bg-orange-600 transition font-bold">
                            💸 {t('Log Advance')}
                        </button>
                        <button onClick={() => setIsAddModalOpen(true)} className="bg-indigo-600 text-white px-4 py-2 rounded shadow hover:bg-indigo-700 transition font-bold">
                            + {t('New Employee')}
                        </button>
                    </>}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-3">
                    <div className="bg-indigo-100 p-3 rounded-full text-xl">💵</div>
                    <div>
                        <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider">{t('Total Branch Payroll')}</p>
                        <h3 className="text-xl font-black text-gray-800">{totalPayroll.toLocaleString()} <span className="text-sm font-normal">CFA</span></h3>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-3">
                    <div className="bg-orange-100 p-3 rounded-full text-xl">⚠️</div>
                    <div>
                        <p className="text-[10px] text-orange-600 font-bold uppercase tracking-wider">{t('Total to Recover')}</p>
                        <h3 className="text-xl font-black text-red-600">{totalToRecover.toLocaleString()} <span className="text-sm font-normal">CFA</span></h3>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-3">
                    <div className="bg-red-100 p-3 rounded-full text-xl">📉</div>
                    <div>
                        <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">{t('Inventory Losses')}</p>
                        <h3 className="text-xl font-black text-red-700">{totalLosses.toLocaleString()} <span className="text-sm font-normal">CFA</span></h3>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border flex items-center gap-3">
                    <div className="bg-yellow-100 p-3 rounded-full text-xl">👥</div>
                    <div>
                        <p className="text-[10px] text-yellow-600 font-bold uppercase tracking-wider">{t('Employees With Debt')}</p>
                        <h3 className="text-xl font-black text-gray-800">{employeesWithDebt} <span className="text-sm font-normal">{t('employees')}</span></h3>
                    </div>
                </div>
            </div>

            {/* Main Table */}
            <DataTable columns={columns} data={employees} />

            {/* ===================== VIEW / LEDGER MODAL ===================== */}
            <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title={selectedEmployee ? `📋 ${selectedEmployee.name}` : ''} size="xl">
                {selectedEmployee && (
                    <div className="space-y-6">
                        {/* Edit Form */}
                        <form onSubmit={handleEditEmp} className="grid grid-cols-2 gap-3">
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('Name')}</label>
                                <input required className="w-full border p-2 rounded mt-1" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('Position')}</label>
                                <input className="w-full border p-2 rounded mt-1" value={editForm.position || ''} onChange={e => setEditForm({ ...editForm, position: e.target.value })} /></div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('Department')}</label>
                                <select className="w-full border p-2 rounded mt-1" value={editForm.department_id} onChange={e => setEditForm({ ...editForm, department_id: e.target.value })}>
                                    <option value="">{t('None')}</option>
                                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('System Role')}</label>
                                <select className="w-full border p-2 rounded mt-1" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                                    <option value="Employee">{t('Employee')}</option>
                                    <option value="Manager">{t('Manager')}</option>
                                </select>
                            </div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('Employment Status')}</label>
                                <select className="w-full border p-2 rounded mt-1" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                                    <option value="Active">{t('Active')}</option>
                                    <option value="On Leave">{t('On Leave')}</option>
                                    <option value="Inactive">{t('Inactive')}</option>
                                </select>
                            </div>
                            <div><label className="block text-xs font-bold text-gray-500 uppercase">{t('Base ref / Notes')}</label>
                                <input type="number" className="w-full border p-2 rounded mt-1" value={editForm.base_salary || 0} onChange={e => setEditForm({ ...editForm, base_salary: e.target.value })} /></div>
                            <div className="col-span-2"><label className="block text-xs font-bold text-gray-500 uppercase">{t('Assigned Branch')}</label>
                                <select
                                    className="w-full border p-2 rounded mt-1"
                                    value={editForm.depo_id || ''}
                                    onChange={e => {
                                        const selectedDepo = depos.find(d => String(d.id) === String(e.target.value));
                                        setEditForm({
                                            ...editForm,
                                            depo_id: e.target.value,
                                        });
                                    }}
                                >
                                    <option value="">{t('None')}</option>
                                    {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                                {editForm.depo_id && (() => {
                                    const d = depos.find(dep => String(dep.id) === String(editForm.depo_id));
                                    return d?.cost_value ? (
                                        <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                                            <span className="text-indigo-400">💵</span>
                                            <div>
                                                <p className="text-[10px] text-indigo-500 font-bold uppercase">{t('Branch Cost Value')}</p>
                                                <p className="text-indigo-800 font-black text-lg">{parseFloat(d.cost_value).toLocaleString()} <span className="text-sm font-normal">CFA</span></p>
                                            </div>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                            {isAdmin && <div className="col-span-2 flex gap-2 pt-2">
                                <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">{t('Save Changes')}</button>
                                <button type="button" className="bg-gray-100 px-4 py-2 rounded" onClick={() => setIsViewModalOpen(false)}>{t('Cancel')}</button>
                            </div>}
                        </form>

                        {/* Account Summary */}
                        <div className="grid grid-cols-3 gap-3 bg-gray-50 p-4 rounded-xl">
                            <div className="text-center"><p className="text-xs text-gray-500 uppercase font-bold">{t('Branch Cost Value')}</p>
                                <p className="text-xl font-black text-indigo-700">{parseFloat(selectedEmployee.depo_cost_value || 0).toLocaleString()} CFA</p></div>
                            <div className="text-center"><p className="text-xs text-gray-500 uppercase font-bold">{t('Total Deductions')}</p>
                                <p className="text-xl font-black text-red-600">-{parseFloat(selectedEmployee.total_deductions || 0).toLocaleString()} CFA</p></div>
                            <div className="text-center"><p className="text-xs text-gray-500 uppercase font-bold">{t('Final Balance')}</p>
                                <p className={`text-xl font-black ${parseFloat(selectedEmployee.balance) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                                    {parseFloat(selectedEmployee.balance || 0).toLocaleString()} CFA</p></div>
                        </div>

                        {/* Advances History */}
                        <div>
                            <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">💸 {t('Manual Advances')}</h4>
                            {selectedEmployee.advances?.length > 0 ? (
                                <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-sm">
                                    <thead><tr className="border-b bg-gray-50 text-left">
                                        <th className="p-2">{t('Date')}</th>
                                        <th className="p-2 text-right">{t('Amount')}</th>
                                        <th className="p-2">{t('Note')}</th>
                                    </tr></thead>
                                    <tbody>{selectedEmployee.advances.map((a, i) => (
                                        <tr key={i} className="border-b">
                                            <td className="p-2 text-gray-500 text-xs">{new Date(a.date).toLocaleDateString()}</td>
                                            <td className="p-2 text-right font-bold text-orange-600">-{parseFloat(a.amount).toLocaleString()} CFA</td>
                                            <td className="p-2 text-gray-500 italic text-xs">{a.note || '—'}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
</div>
                            ) : <p className="text-sm text-gray-400 italic">{t('No advances recorded')}</p>}
                        </div>

                        {/* Losses History */}
                        <div>
                            <h4 className="font-bold text-gray-700 mb-2 flex items-center gap-2">📉 {t('Loss / Theft Deductions')}</h4>
                            {selectedEmployee.losses?.length > 0 ? (
                                <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-sm">
                                    <thead><tr className="border-b bg-gray-50 text-left">
                                        <th className="p-2">{t('Date')}</th>
                                        <th className="p-2">{t('Product')}</th>
                                        <th className="p-2">{t('Qty')}</th>
                                        <th className="p-2 text-right">{t('Amount')}</th>
                                        <th className="p-2">{t('Reason')}</th>
                                        <th className="p-2">{t('Source')}</th>
                                    </tr></thead>
                                    <tbody>{selectedEmployee.losses.map((l, i) => (
                                        <tr key={i} className="border-b">
                                            <td className="p-2 text-gray-500 text-xs">{new Date(l.date).toLocaleDateString()}</td>
                                            <td className="p-2 font-medium">{l.product_name || '—'}</td>
                                            <td className="p-2 text-gray-500">{l.quantity ? `${l.quantity} ${l.unit}` : '—'}</td>
                                            <td className="p-2 text-right font-bold text-red-600">-{parseFloat(l.amount).toLocaleString()} CFA</td>
                                            <td className="p-2 text-gray-500 italic text-xs">{l.reason || '—'}</td>
                                            <td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${l.source === 'Inventory Adjustment' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>{l.source}</span></td>
                                        </tr>
                                    ))}</tbody>
                                </table>
</div>
                            ) : <p className="text-sm text-gray-400 italic">{t('No loss records')}</p>}
                        </div>
                    </div>
                )}
            </Modal>

            {/* ===================== ADD EMPLOYEE MODAL ===================== */}
            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('Add New Employee')}>
                <form onSubmit={handleAddEmp} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium">{t('Select User')}</label>
                        <select
                            required
                            className="w-full border p-2 rounded mt-1 font-medium"
                            value={empForm.name}
                            onChange={e => {
                                const selectedUser = systemUsers.find(u => u.name === e.target.value);
                                setEmpForm({ 
                                    ...empForm, 
                                    name: e.target.value,
                                    position: selectedUser?.role || empForm.position
                                });
                            }}
                        >
                            <option value="">{t('Select a system user...')}</option>
                            {systemUsers.map(u => (
                                <option key={u.id} value={u.name}>
                                    {u.name} (@{u.username}) — {u.role}
                                </option>
                            ))}
                        </select>
                        <p className="text-[10px] text-gray-400 mt-1">{t('Only existing system users can be added as employees')}</p>
                    </div>
                    <div><label className="block text-sm font-medium">{t('Position / Title')}</label>
                        <input className="w-full border p-2 rounded mt-1" value={empForm.position} onChange={e => setEmpForm({ ...empForm, position: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium">{t('Department')}</label>
                            <select className="w-full border p-2 rounded mt-1" value={empForm.department_id} onChange={e => setEmpForm({ ...empForm, department_id: e.target.value })}>
                                <option value="">{t('None')}</option>
                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium">{t('Sys Role')}</label>
                            <select className="w-full border p-2 rounded mt-1" value={empForm.role} onChange={e => setEmpForm({ ...empForm, role: e.target.value })}>
                                <option value="Employee">{t('Employee')}</option>
                                <option value="Manager">{t('Manager')}</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium">{t('Assign to Branch')}</label>
                        <select
                            className="w-full border p-2 rounded mt-1"
                            value={empForm.depo_id}
                            onChange={e => {
                                const selectedDepo = depos.find(d => String(d.id) === String(e.target.value));
                                setEmpForm({ 
                                    ...empForm, 
                                    depo_id: e.target.value,
                                    base_salary: selectedDepo?.cost_value || empForm.base_salary
                                });
                            }}
                        >
                            <option value="">{t('None')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                        {empForm.depo_id && (() => {
                            const d = depos.find(dep => String(dep.id) === String(empForm.depo_id));
                            return d?.cost_value ? (
                                <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                                    <span className="text-indigo-400 text-sm">💵</span>
                                    <div>
                                        <p className="text-[10px] text-indigo-500 font-bold uppercase tracking-wider">{t('Branch Cost Value (auto-detected)')}</p>
                                        <p className="text-indigo-800 font-black text-lg">{parseFloat(d.cost_value).toLocaleString()} <span className="text-sm font-normal">CFA</span></p>
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </div>
                    <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700">{t('Create Employee')}</button>
                </form>
            </Modal>

            {/* ===================== LOG ADVANCE MODAL ===================== */}
            <Modal isOpen={isAdvanceModalOpen} onClose={() => setIsAdvanceModalOpen(false)} title={`💸 ${t('Log Manual Advance')}`}>
                <form onSubmit={handleAddAdvance} className="space-y-4">
                    <div><label className="block text-sm font-medium">{t('Employee')}</label>
                        <select required className="w-full border p-2 rounded mt-1" value={advForm.employee_id} onChange={e => setAdvForm({ ...advForm, employee_id: e.target.value })}>
                            <option value="">{t('Select Employee...')}</option>
                            {employees.map(e => <option key={e.id} value={e.id}>{e.name} {e.depo_name ? `— ${e.depo_name}` : ''}</option>)}
                        </select></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="block text-sm font-medium">{t('Amount (CFA)')}</label>
                            <input required type="number" min="1" step="0.01" className="w-full border p-2 rounded mt-1 bg-orange-50"
                                value={advForm.amount} onChange={e => setAdvForm({ ...advForm, amount: e.target.value })} /></div>
                        <div><label className="block text-sm font-medium">{t('Date')}</label>
                            <input type="date" className="w-full border p-2 rounded mt-1" value={advForm.date} onChange={e => setAdvForm({ ...advForm, date: e.target.value })} /></div>
                    </div>
                    <div><label className="block text-sm font-medium">{t('Note / Reason')}</label>
                        <input className="w-full border p-2 rounded mt-1" placeholder={t('e.g. Personal loan, Emergency advance...')}
                            value={advForm.note} onChange={e => setAdvForm({ ...advForm, note: e.target.value })} /></div>
                    <button type="submit" className="w-full bg-orange-500 text-white py-2 rounded font-bold hover:bg-orange-600">💸 {t('Record Advance')}</button>
                </form>
            </Modal>

            {/* ===================== LOG LOSS / THEFT MODAL ===================== */}
            <Modal isOpen={isLossModalOpen} onClose={() => setIsLossModalOpen(false)} title={`🔴 ${t('Log Loss / Theft Deduction')}`} size="lg">
                <form onSubmit={handleAddLoss} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="block text-sm font-medium">{t('Responsible Employee')}</label>
                            <select required className="w-full border p-2 rounded mt-1" value={lossForm.employee_id} onChange={e => setLossForm({ ...lossForm, employee_id: e.target.value })}>
                                <option value="">{t('Select Employee...')}</option>
                                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select></div>
                        <div><label className="block text-sm font-medium">{t('Depot Branch')}</label>
                            <select className="w-full border p-2 rounded mt-1" value={lossForm.depo_id} onChange={e => setLossForm({ ...lossForm, depo_id: e.target.value })}>
                                <option value="">{t('Select Branch...')}</option>
                                {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select></div>
                    </div>

                    {/* Product Search */}
                    <div className="relative">
                        <label className="block text-sm font-medium">{t('Product (Search)')} <span className="text-gray-400 text-xs">{t('optional')}</span></label>
                        <input
                            type="text"
                            className="w-full border p-2 rounded mt-1"
                            placeholder={t('Search product by name or barcode...')}
                            value={lossSearchTerm}
                            onChange={e => { setLossSearchTerm(e.target.value); }}
                        />
                        {lossProducts.length > 0 && (
                            <div className="absolute z-10 w-full bg-white border shadow-lg rounded mt-1 max-h-40 overflow-y-auto">
                                {lossProducts.map(p => (
                                    <div key={p.id} className="p-2 hover:bg-red-50 cursor-pointer text-sm border-b" onClick={() => {
                                        setLossForm({ ...lossForm, product_id: p.id, product_name: p.name, amount: p.price || '' });
                                        setLossSearchTerm(p.name);
                                        setLossProducts([]);
                                    }}>
                                        <span className="font-medium">{p.name}</span> <span className="text-gray-400 text-xs">— {p.price ? `${parseFloat(p.price).toLocaleString()} CFA` : 'no price'}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div><label className="block text-sm font-medium">{t('Quantity')}</label>
                            <input type="number" min="0" step="0.01" className="w-full border p-2 rounded mt-1"
                                value={lossForm.quantity} onChange={e => setLossForm({ ...lossForm, quantity: e.target.value })} /></div>
                        <div><label className="block text-sm font-medium">{t('Unit')}</label>
                            <select className="w-full border p-2 rounded mt-1" value={lossForm.unit} onChange={e => setLossForm({ ...lossForm, unit: e.target.value })}>
                                <option value="unit">{t('Unit')}</option>
                                <option value="box">{t('Box')}</option>
                                <option value="carton">{t('Carton')}</option>
                                <option value="piece">{t('Piece')}</option>
                            </select></div>
                        <div><label className="block text-sm font-medium text-red-600 font-bold">{t('Loss Amount (CFA)')}</label>
                            <input required type="number" min="1" step="0.01" className="w-full border p-2 rounded mt-1 bg-red-50"
                                value={lossForm.amount} onChange={e => setLossForm({ ...lossForm, amount: e.target.value })} /></div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="block text-sm font-medium">{t('Source')}</label>
                            <select className="w-full border p-2 rounded mt-1" value={lossForm.source} onChange={e => setLossForm({ ...lossForm, source: e.target.value })}>
                                <option value="Manual Entry">{t('Manual Entry')}</option>
                                <option value="Inventory Adjustment">{t('Inventory Adjustment')}</option>
                            </select></div>
                        <div><label className="block text-sm font-medium">{t('Date')}</label>
                            <input type="date" className="w-full border p-2 rounded mt-1" value={lossForm.date} onChange={e => setLossForm({ ...lossForm, date: e.target.value })} /></div>
                    </div>

                    <div><label className="block text-sm font-medium">{t('Reason')}</label>
                        <select className="w-full border p-2 rounded mt-1 mb-2" onChange={e => setLossForm({ ...lossForm, reason: e.target.value === 'custom' ? '' : e.target.value })}>
                            <option value="">{t('Select a reason...')}</option>
                            <option value="Missing box after monthly inventory check">{t('Missing box after monthly inventory check')}</option>
                            <option value="Theft detected">{t('Theft detected')}</option>
                            <option value="Damaged during transport">{t('Damaged during transport')}</option>
                            <option value="Stock mismatch">{t('Stock mismatch')}</option>
                            <option value="Unexplained loss">{t('Unexplained loss')}</option>
                            <option value="custom">{t('Custom reason...')}</option>
                        </select>
                        <input className="w-full border p-2 rounded" placeholder={t('Or type reason here...')}
                            value={lossForm.reason} onChange={e => setLossForm({ ...lossForm, reason: e.target.value })} />
                    </div>

                    <button type="submit" className="w-full bg-red-600 text-white py-3 rounded font-bold hover:bg-red-700 shadow-lg">
                        🔴 {t('Record Loss / Theft Deduction')}
                    </button>
                </form>
            </Modal>

            <Modal isOpen={confirmDialog.isOpen} onClose={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })} title={t('Confirm Action')} size="sm">
                <div className="space-y-4">
                    <p className="text-gray-700 font-medium">{confirmDialog.message}</p>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 font-bold" onClick={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}>{t('Cancel')}</button>
                        <button className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold" onClick={confirmDialog.onConfirm}>{t('Confirm')}</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Employees;
